import { api } from "@/lib/api";
import { locale } from "@/lib/i18n";

/** Сделки и воронки (backend/hub/api/deals.py). Сделки из Zoho CRM обновляются синхронизацией; правка в Hub главнее. */

export type StageKind = "open" | "won" | "lost";

export type DealStage = { id: string; pipelineId: string; name: string; kind: StageKind; color: string; position: number; count: number; sum: number };

export type Pipeline = { id: string; name: string; isDefault: boolean; zohoName: string | null; stages: DealStage[] };

export type Deal = {
  id: string;
  title: string;
  pipelineId: string;
  stageId: string;
  stage: { id: string; name: string; kind: StageKind; color: string } | null;
  amount: number | null;
  currency: string;
  contactId: string | null;
  contact: { id: string; name: string; phone: string | null; email: string | null } | null;
  companyId: string | null;
  company: { id: string; name: string } | null;
  ownerId: string | null;
  owner: string | null;
  salesperson: string;
  source: string;
  utm: Record<string, string> | null;
  closingDate: string | null;
  lostReason: string;
  description: string;
  nextStep: string;
  zcrmDealId: string | null;
  zcrmUrl: string | null;
  stageChangedAt: string | null;
  daysInStage: number;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  history?: { from: string; to: string; at: string; by: string }[];
  /** заказы и счета Zoho Books, привязанные к сделке (zcrm_potential_id) */
  orders?: BooksDoc[];
};

export type BooksDoc = {
  id: string;
  kind: "salesorder" | "invoice" | "payment" | "creditnote";
  number: string;
  reference: string;
  customer: string;
  customerBooksId: string | null;
  date: string | null;
  dueDate: string | null;
  status: string;
  total: number;
  balance: number;
  currency: string;
  salesperson: string;
  mode: string;
  invoiceNumbers: string[];
  dealId: string | null;
  booksUrl: string;
};

export type DealInput = Partial<{
  title: string;
  pipelineId: string;
  stageId: string;
  amount: number | null;
  currency: string;
  contactId: string | null;
  companyId: string | null;
  ownerId: string | null;
  source: string;
  closingDate: string | null;
  lostReason: string;
  description: string;
  nextStep: string;
}>;

export type Board = { pipeline: { id: string; name: string }; stages: (DealStage & { items: Deal[] })[] };

export type DealQuery = { pipeline?: string; stage?: string; status?: string; owner?: string; contact?: string; company?: string; q?: string; page?: number; size?: number };

const qs = (o: Record<string, unknown>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
};

export const dealsApi = {
  pipelines: () => api<Pipeline[]>("/pipelines"),
  createPipeline: (name: string) => api<Pipeline>("/pipelines", { method: "POST", json: { name } }),
  updatePipeline: (id: string, body: { name?: string; isDefault?: boolean }) => api<Pipeline>(`/pipelines/${id}`, { method: "PATCH", json: body }),
  removePipeline: (id: string) => api(`/pipelines/${id}`, { method: "DELETE" }),
  createStage: (pipelineId: string, body: { name: string; kind?: StageKind; color?: string; afterId?: string | null }) =>
    api<DealStage>(`/pipelines/${pipelineId}/stages`, { method: "POST", json: body }),
  updateStage: (id: string, body: { name?: string; kind?: StageKind; color?: string; afterId?: string | null }) => api<DealStage>(`/pipelines/stages/${id}`, { method: "PATCH", json: body }),
  removeStage: (id: string) => api(`/pipelines/stages/${id}`, { method: "DELETE" }),
  list: (q: DealQuery = {}) => api<{ items: Deal[]; total: number }>(`/deals${qs(q)}`),
  board: (q: { pipeline?: string; owner?: string; q?: string; limit?: number } = {}) => api<Board>(`/deals/board${qs(q)}`),
  get: (id: string) => api<Deal>(`/deals/${id}`),
  create: (body: DealInput) => api<Deal>("/deals", { method: "POST", json: body }),
  update: (id: string, body: DealInput) => api<Deal>(`/deals/${id}`, { method: "PATCH", json: body }),
  remove: (id: string) => api(`/deals/${id}`, { method: "DELETE" }),
};

export const money = (n: number | null | undefined, cur = "AED") => (n === null || n === undefined ? "—" : `${cur} ${Math.round(n).toLocaleString(locale)}`);

/** открыть сделку (или форму новой сделки) из другого раздела — карточки контакта, компании */
let pendingDeal: string | null = null;
let pendingNew: DealInput | null = null;
export const showDeal = (id: string) => {
  pendingDeal = id;
};
export const takeDeal = () => {
  const id = pendingDeal;
  pendingDeal = null;
  return id;
};
export const showNewDeal = (draft: DealInput) => {
  pendingNew = draft;
};
export const takeNewDeal = () => {
  const d = pendingNew;
  pendingNew = null;
  return d;
};
